import { PropsWithChildren, useEffect } from 'react';
import {
    IPublicClientApplication,
    EventType,
    EventMessage,
    AuthenticationResult,
    AuthError,
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { useHistory } from 'react-router-dom';
import { CustomNavigationClient } from 'startup/custom-navigation-client';
import { useAppState } from 'shared/stores';
import { useErrorHandler } from 'react-error-boundary';
import { getUserInfoFromIdClaims, checkPermissions } from 'shared/utility';
import { PERMISSIONS } from 'shared/constants';
import { InsightB2CClaims } from 'shared/types';
import { useSnackbar } from 'notistack';
import { Typography } from '@mui/material';

type AuthProviderProps = {
    clientApp: IPublicClientApplication;
};

export function ProvideAuth({ children, clientApp }: PropsWithChildren<AuthProviderProps>) {
    const history = useHistory();
    const appState = useAppState();
    const handleError = useErrorHandler();
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        const navigationClient = new CustomNavigationClient(history);
        clientApp.setNavigationClient(navigationClient);
        const accounts = clientApp.getAllAccounts();
        if (accounts.length > 0) {
            const account = accounts[0];
            clientApp.setActiveAccount(account);
            const user = getUserInfoFromIdClaims(account.idTokenClaims as InsightB2CClaims);
            appState.user.set(user);
        }

        clientApp.addEventCallback((event: EventMessage) => {
            if (event.payload) {
                switch (event.eventType) {
                    case EventType.LOGIN_SUCCESS:
                        const payload = event.payload as AuthenticationResult;
                        // Verify Insight claims here.
                        const account = payload.account;
                        clientApp.setActiveAccount(account);
                        const idTokenClaims = account?.idTokenClaims as InsightB2CClaims;
                        const hasPermission = checkPermissions(idTokenClaims.InsightClaims);
                        if (!hasPermission(PERMISSIONS.login)) {
                            enqueueSnackbar(
                                'Login Successful, but you do not have permission to access this application.\nlogging out now...',
                                { variant: 'error' }
                            );
                            setTimeout(() => {
                                clientApp.logout();
                            }, 3500);
                        }
                        const user = getUserInfoFromIdClaims(idTokenClaims);
                        appState.user.set(user);
                        break;
                    case EventType.ACQUIRE_TOKEN_SUCCESS:
                        const authResult = event.payload as AuthenticationResult;
                        if (!authResult.fromCache) {
                            // verify insight claims
                            // console.log('New Token ', event.payload);
                        }
                        break;
                    case EventType.LOGOUT_END:
                        appState.user.set(null);
                        break;
                    case EventType.LOGIN_FAILURE:
                    case EventType.ACQUIRE_TOKEN_FAILURE:
                    case EventType.LOGOUT_FAILURE:
                        const authError = event.error as AuthError;
                        handleError(authError);
                        break;
                    default:
                        break;
                }
            }
        });
    }, [clientApp, history, appState, handleError, enqueueSnackbar]);

    return <MsalProvider instance={clientApp}>{children}</MsalProvider>;
}

export const AuthInProgress = () => <Typography variant='h4'>Authorization in progress...</Typography>;
